import { useQuery, queryOptions } from '@tanstack/react-query'
import * as fieldDefinitionsService from '@/services/field-definitions.service'
import type { ViewEntityType, FieldDefinitionSearch } from '@/types/api'

// =============================================================================
// Query Keys Factory
// =============================================================================

export const fieldDefinitionsKeys = {
  all: ['field-definitions'] as const,
  search: (params: FieldDefinitionSearch) =>
    [...fieldDefinitionsKeys.all, 'search', params] as const,
  byEntity: (entityType: ViewEntityType) =>
    [...fieldDefinitionsKeys.all, 'entity', entityType] as const,
  details: () => [...fieldDefinitionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...fieldDefinitionsKeys.details(), id] as const,
}

// =============================================================================
// Query Options
// =============================================================================

const FIELD_DEFINITIONS_STALE_TIME = 300000 // 5 minutes (rarely changes)
const FIELD_DEFINITIONS_GC_TIME = 600000 // 10 minutes

export const fieldDefinitionsSearchQueryOptions = (
  params: FieldDefinitionSearch
) =>
  queryOptions({
    queryKey: fieldDefinitionsKeys.search(params),
    queryFn: () => fieldDefinitionsService.searchFieldDefinitions(params),
    staleTime: FIELD_DEFINITIONS_STALE_TIME,
    gcTime: FIELD_DEFINITIONS_GC_TIME,
  })

export const fieldDefinitionsByEntityQueryOptions = (
  entityType: ViewEntityType
) =>
  queryOptions({
    queryKey: fieldDefinitionsKeys.byEntity(entityType),
    queryFn: () =>
      fieldDefinitionsService.getFieldDefinitionsByEntity(entityType),
    staleTime: FIELD_DEFINITIONS_STALE_TIME,
    gcTime: FIELD_DEFINITIONS_GC_TIME,
  })

export const fieldDefinitionQueryOptions = (id: string | null) =>
  queryOptions({
    queryKey: fieldDefinitionsKeys.detail(id ?? ''),
    queryFn: () => (id ? fieldDefinitionsService.getFieldDefinition(id) : null),
    enabled: !!id,
    staleTime: FIELD_DEFINITIONS_STALE_TIME,
    gcTime: FIELD_DEFINITIONS_GC_TIME,
  })

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to fetch field definitions for an entity type
 */
export function useFieldDefinitions(entityType: ViewEntityType) {
  const query = useQuery(fieldDefinitionsByEntityQueryOptions(entityType))

  return {
    fieldDefinitions: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to search field definitions with custom params
 */
export function useFieldDefinitionsSearch(params: FieldDefinitionSearch) {
  return useQuery(fieldDefinitionsSearchQueryOptions(params))
}

/**
 * Hook to fetch a single field definition by ID
 */
export function useFieldDefinition(id: string | null) {
  return useQuery(fieldDefinitionQueryOptions(id))
}
